/**
 * ViewControl (docs/04 §5) — table or cards, as a two-option segmented control
 * matching DensityControl's shape and semantics.
 *
 * It is a SEPARATE control from density on purpose (ADR-27). Density is "how
 * much fits"; view is "what shape the records take". The v1 beta coupled them,
 * which meant nobody could ask for dense cards or roomy rows, and it made the
 * ">=30% more rows in compact" target untestable because the two density modes
 * stopped being the same table.
 */

import { type TableView } from '@propos/shared';
import { clsx } from 'clsx';
import { LayoutGrid, Rows3 } from 'lucide-react';
import type { ReactNode } from 'react';

const OPTIONS: readonly {
  readonly value: TableView;
  readonly label: string;
  readonly icon: ReactNode;
}[] = [
  { value: 'table', label: 'Table', icon: <Rows3 size={15} aria-hidden /> },
  { value: 'cards', label: 'Cards', icon: <LayoutGrid size={15} aria-hidden /> },
];

export function ViewControl({
  view,
  onChange,
}: {
  readonly view: TableView;
  readonly onChange: (next: TableView) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label="List layout"
      className="flex shrink-0 rounded-ctl border border-card-line bg-canvas p-0.5"
    >
      {OPTIONS.map((option) => {
        const active = option.value === view;
        return (
          <button
            key={option.value}
            type="button"
            // The label is hidden at narrow widths, so the accessible name comes
            // from aria-label — the same lesson the density control's axe
            // failure taught (docs/04 §5).
            aria-label={option.label}
            aria-pressed={active}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            className={clsx(
              'inline-flex min-h-9 items-center gap-1.5 rounded-[6px] px-2.5 text-meta font-medium',
              'transition-colors duration-(--dur-fast)',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700',
              active ? 'bg-card text-ink shadow-sm' : 'text-ink2 hover:text-ink',
            )}
          >
            {option.icon}
            <span aria-hidden className="hidden md:inline">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
